import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  getGamesSessions,
  scoresQuery,
  scoresStatsQuery
} from './api';

import PopupMessages from '../UI/introGameLayout/popupMessages';
import Summary from '../UI/game/summary';
import PerformanceRankingInfo from '../UI/performanceRankingInfo';

import './styles.css';

const calculateScoreStats = (scores) => {
  if (!scores || !scores.length) return { points: 0, accuracy: 0, averageTime: 0 };
  
  const calculations = scores.reduce((acc, scoresStat) => {
    return {
      totalPoints: acc.totalPoints + scoresStat.total_points,
      totalAccuracy: acc.totalAccuracy + scoresStat.total_accuracy,
      totalAverageTime: acc.totalAverageTime + calculateTimeInSeconds(scoresStat.average_time),
      count: acc.count + 1
    };
  }, { totalPoints: 0, totalAccuracy: 0, totalAverageTime: 0, count: 0 });

  
  const totalPoints = calculations.totalPoints;
  const averageAccuracy = calculations.count > 0 ? (calculations.totalAccuracy / calculations.count) : 0;
  const averageAverageTime = calculations.count > 0 ? (calculations.totalAverageTime / calculations.count).toFixed(2) : 0;

  return {points: totalPoints, accuracy: averageAccuracy, averageTime: averageAverageTime};
};

export default function NormalBalanceSummaryFinal() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const isAfterFeedback = searchParams.has('after-feedback');
  
  const {
    getUser,
    getCurrentNormalGameId,
    saveCurrentGameId,
    isInstructor
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentNormalGameId();

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);
  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);
  const [firstAttemptGameSessionId, setFirstAttemptGameSessionId] = useState(0);

  const [rank, setRank] = useState('');

  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [firstAttemptPoints, setFirstAttemptPoints] = useState(0);
  const [firstAttemptAccuracy, setFirstAttemptAccuracy] = useState(0);
  const [firstAttemptAverageTime, setFirstAttemptAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);

  const [noButtonText, setNoButtonText] = useState('No');

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummary'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));
  const { data: scores } = useQuery(scoresQuery({ gameId, userId: user?.id, gameSessionId: currentGameSessionId }));
  const { data: firstAttemptScores } = useQuery(scoresQuery({ gameId, userId: user?.id, gameSessionId: firstAttemptGameSessionId }));

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }

      if(currentGameSessions.length > 1) {
        const firstAttemptGameSession = currentGameSessions.find(session => session.attempt === 1);
        setFirstAttemptGameSessionId(firstAttemptGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(scores && scoresStats) {
      const calculatedScores = calculateScoreStats(scores);

      setPoints(calculatedScores.points);
      setAccuracy(calculatedScores.accuracy);
      setAverageTime(calculatedScores.averageTime);

      // Rank is computed server-side and exposed as session.rank_session,
      // matching what normal-balance/badge and normal-balance/summary
      // display. Reading it from the same source keeps all three pages
      // in sync.
      setRank(scoresStats.session?.rank_session || '');
    }
  }, [scores, scoresStats]);
  
  useEffect(() => {
    if(firstAttemptScores) {
      const calculatedScores = calculateScoreStats(firstAttemptScores);

      setFirstAttemptPoints(calculatedScores.points);
      setFirstAttemptAccuracy(calculatedScores.accuracy);
      setFirstAttemptAverageTime(calculatedScores.averageTime);
    }
  }, [firstAttemptScores]);

  useEffect(() => {
    if (scores) {
      setDataLoaded(true);
    }
  }, [scores]);

  useEffect(() => {
    if (currentGameSessionAttempt >= 3) {
      setNoButtonText('Close');
    }
  }, [currentGameSessionAttempt]);

  const handleYesButtonClick = () => {
    saveCurrentGameId(gameId, 'Normal Balance');
    navigate('/acc/transition');
  }
  
  const handleNoButtonClick = () => {
    // Instructors land on /assignments (a student-facing page) when
    // they preview a game and click "No" on summary-final. Send them
    // to their own assignments listing instead so the page they hit
    // matches their role.
    navigate(isInstructor() ? '/instructor/settings/assignments' : '/assignments');
  }

  const popupMessages = useMemo(() => {
    const initialTitle = isAfterFeedback ? '<h2 class="align-center">Thanks for the feedback!</h2>' : '<h2 class="align-center">That’s all for now!</h2>';
    const messages = [{
      name: 'Bailey',
      text: initialTitle
    }];
    let additionalText = '';
    
    if (currentGameSessionAttempt < 3) {
      additionalText += '<p class="align-center">Would you like to try again to improve your score?</p><br>';
    }
    if (additionalText) {
      messages[0] = {
        ...messages[0],
        text: messages[0].text + additionalText
      };
    }
    return messages;
  }, [currentGameSessionAttempt]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }
  
  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <div className="summary-popup-messages-container">
              <PopupMessages
                messages={popupMessages}
                messageSize="small"
              />
              <div className="summary-final-buttons">
                {
                  currentGameSessionAttempt < 3 &&
                    <button className="button-navi" onClick={handleYesButtonClick}>Yes</button>
                    
                }
                <button className="button-navi" onClick={handleNoButtonClick}>{noButtonText}</button>
              </div>
            </div>
            <Summary 
              rank={rank}
              points={points}
              firstAttemptPoints={firstAttemptPoints}
              lines={[
                {label: 'Accuracy', value: accuracy, firstAttemptValue: firstAttemptAccuracy},
                {label: 'Average Time', value: averageTime, firstAttemptValue: firstAttemptAverageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
